import type { CourierSettings } from '@shared/sdk-v1';

/**
 * CourierSettings columns the API treats as secrets. Keep in sync with
 * legacy-api common/utils/courier-settings-secrets.js (SECRET_FIELDS).
 *
 * The API never returns these values. It emits a computed `has<Field>` boolean
 * instead, but only when the secret column is selected in the query `fields`,
 * so the courierSlice whitelists must keep naming them.
 */
export const SECRET_FIELDS = [
  'accountSid',
  'airMeetsSeaApiKey',
  'atoraApiKey',
  'authToken',
  'cargoJaApiKey',
  'ezeePayLicenceKey',
  'farin2YaadApiKey',
  'fcmServerKey',
  'fcmServiceAccount',
  'fygaroApiKey',
  'fygaroApiSecretKey',
  'hnmPassword',
  'jaShippersApiKey',
  'logisApiAccessToken',
  'onePlusApiKey',
  'onePlusWebhookKey',
  'privateVapidKey',
  'sccApiToken',
  'sccWebhookKey',
  'sflApiKey',
  'shipBizApiKey',
  'shipCorpApiAccessToken',
  'skgApiKey',
  'speedMasterzApiKey',
  'speedzMasterV2ApiKey',
  'ssnApiKey',
  'ssnPartnerApiToken',
  'ssnWebhookSecret',
  'tasokoApiAccessToken',
  'trGlobalApiToken',
  'trGlobalWebhookKey',
] as const;

export type SecretField = (typeof SECRET_FIELDS)[number];

/** Sending exactly this value for a secret clears it server-side. Blank keeps it. */
export const CLEAR_SENTINEL = '__CLEAR__';

/** fygaroApiKey -> hasFygaroApiKey */
export function hasFlagName(field: string): string {
  return 'has' + field.charAt(0).toUpperCase() + field.slice(1);
}

/**
 * Whether a secret is configured. Prefers the server flag and falls back to the
 * raw value so it works against both the old and new API during rollout.
 */
export function hasSecret(
  settings: Partial<CourierSettings> | null | undefined,
  field: SecretField
): boolean {
  if (!settings) return false;
  const bag = settings as unknown as Record<string, unknown>;
  const flag = bag[hasFlagName(field)];
  if (typeof flag === 'boolean') return flag;
  return !!bag[field];
}

const VENDOR_API_KEY_FIELDS: SecretField[] = [
  'jaShippersApiKey',
  'shipCorpApiAccessToken',
  'shipBizApiKey',
  'skgApiKey',
];

/** True when any of the classic vendor API keys is configured. */
export function hasAnyVendorApiKey(
  settings: Partial<CourierSettings> | null | undefined,
  includeOnePlus = false
): boolean {
  const fields = includeOnePlus
    ? [...VENDOR_API_KEY_FIELDS, 'onePlusApiKey' as SecretField]
    : VENDOR_API_KEY_FIELDS;
  return fields.some((f) => hasSecret(settings, f));
}

/** Builds { has<Field>: boolean } for the given secrets, e.g. to seed a form. */
export function secretFlagsFrom(
  settings: Partial<CourierSettings> | null | undefined,
  fields: readonly SecretField[]
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const f of fields) out[hasFlagName(f)] = hasSecret(settings, f);
  return out;
}
